/**
 * AutoCAD Color Index (ACI) → RGB (작업지시서 P3-02)
 *
 * DXF 는 색을 0~256 정수로 적는다.
 *   0   = ByBlock
 *   256 = ByLayer   ← 실무 도면 대부분이 이 값이다
 *   1~9 = 기본 색상
 *   10~249 = 24색상 × 10명도 팔레트
 *   250~255 = 무채색
 *
 * 10~249 구간은 규칙적인 팔레트라 수식으로 근사한다.
 * 마트 레이아웃에서 색은 레이어가 결정하므로(ByLayer) 이 구간의 정확도는
 * 실무상 중요도가 낮다 — 정확도보다 코드 크기를 택했다.
 */

/** 기본 9색 + 무채색 6색은 정확히 매핑한다 (실무에서 실제로 쓰이는 값) */
const EXACT: Record<number, string> = {
  1: '#FF0000', // red
  2: '#FFFF00', // yellow
  3: '#00FF00', // green
  4: '#00FFFF', // cyan
  5: '#0000FF', // blue
  6: '#FF00FF', // magenta
  7: '#FFFFFF', // white/black (배경에 따라 반전 — 우리는 어두운 배경이므로 흰색)
  8: '#808080',
  9: '#C0C0C0',
  250: '#333333',
  251: '#5B5B5B',
  252: '#848484',
  253: '#ADADAD',
  254: '#D6D6D6',
  255: '#FFFFFF',
};

/** ByLayer — 레이어 색을 따른다는 뜻. 엔티티 색을 null 로 둔다. */
export const ACI_BY_LAYER = 256;
export const ACI_BY_BLOCK = 0;

export function aciToHex(index: number): string | null {
  if (index === ACI_BY_LAYER || index === ACI_BY_BLOCK) return null; // ByLayer/ByBlock
  if (EXACT[index]) return EXACT[index]!;
  if (index < 10 || index > 249) return null;

  // 24 색상 × 10 명도. hue 는 15° 간격.
  const group = Math.floor((index - 10) / 10); // 0..23
  const shade = (index - 10) % 10; // 0..9
  const hue = group * 15;

  // 짝수 shade = 채도 100%, 홀수 = 50%. 명도는 shade 가 커질수록 어두워진다.
  const sat = shade % 2 === 0 ? 1 : 0.5;
  const val = 1 - Math.floor(shade / 2) * 0.18;

  return hsvToHex(hue, sat, Math.max(0.15, val));
}

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const hex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** hex → 가장 가까운 ACI. 쓰기 시 사용. 정확도보다 안정성을 택한다. */
export function hexToAci(hex: string | null | undefined): number {
  if (!hex) return ACI_BY_LAYER;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return ACI_BY_LAYER;

  const v = Number.parseInt(m[1]!, 16);
  const r = (v >> 16) & 255;
  const g = (v >> 8) & 255;
  const b = v & 255;

  let best = 7;
  let bestD = Infinity;
  for (const [k, val] of Object.entries(EXACT)) {
    const t = Number.parseInt(val.slice(1), 16);
    const dr = ((t >> 16) & 255) - r;
    const dg = ((t >> 8) & 255) - g;
    const db = (t & 255) - b;
    const d = dr * dr + dg * dg + db * db;
    if (d < bestD) {
      bestD = d;
      best = Number(k);
    }
  }
  return best;
}
